/**
 * FirstSquadOnboarding — first-time educational banner that lands
 * above the active squad banners when a new operator sees their
 * first auto-dispatch.
 *
 * Per cycle-2 UX F10 + spec §13.5. Default ON; operator clicks
 * "OK, got it" to dismiss (acked state persists in localStorage).
 *
 * Per SP4 §0.6: backend has NO `users.metadata` JSONB column. To
 * avoid a migration for a single onboarding bit, persistence uses
 * localStorage. Operators clearing localStorage will see the banner
 * again — acceptable v0 behavior.
 *
 * Rev 3 / Product M1: the "Settings: never auto-summon" button was
 * previously a dead-end (no onOpenSettings wiring). Now POSTs to
 * /api/sessions/{id}/autonomy/disable-squads (route shipped in
 * C.2c) and acknowledges on success.
 *
 * Rev 3 / Product S1: vocabulary unification — "respond on their
 * side spine" (was "their own spine"; matches the canonical diagram
 * + ChatMap label set).
 */
import React from 'react';
import { useToast } from '../../contexts/ToastContext';
import type { Squad, SquadMember } from '../../types/squads';


interface FirstSquadOnboardingProps {
  squad: Squad;
  members: SquadMember[];
  sessionId: string;
  onAcknowledge: () => void;
}


export const FirstSquadOnboarding: React.FC<FirstSquadOnboardingProps> = ({
  squad, members, sessionId, onAcknowledge,
}) => {
  const { showToast } = useToast();
  const memberPersonas = members.slice(0, 3).map((m) => m.persona_id).join(', ')
    + (members.length > 3 ? ` and ${members.length - 3} more` : '');

  const handleDisableAutoSummon = async () => {
    try {
      const res = await fetch(
        `/api/sessions/${sessionId}/autonomy/disable-squads`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) {
        throw new Error(`status ${res.status}`);
      }
      showToast('Auto-summon disabled for this session', 'success');
      onAcknowledge();
    } catch (e) {
      showToast('Failed to disable auto-summon', 'error');
    }
  };

  return (
    <div
      data-first-squad-onboarding
      className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200
                 dark:border-indigo-800 rounded p-3 shrink-0"
    >
      <div className="text-sm font-medium text-indigo-900 dark:text-indigo-100">
        ✨ Heads up — Trailmaster just summoned {members.length}{' '}
        persona{members.length === 1 ? '' : 's'}
      </div>
      <div className="text-xs text-indigo-700 dark:text-indigo-300 mt-1">
        Trailmaster summoned {memberPersonas} to look at this
        (~${squad.total_estimated_cost_usd.toFixed(2)} estimate). Each will
        respond on their side spine; Trailmaster synthesizes when all done.
        Cost is server-capped; recall any time via the banner.
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <button
          type="button"
          onClick={handleDisableAutoSummon}
          className="text-xs text-amber-600 hover:text-amber-700
                     dark:text-amber-400 dark:hover:text-amber-300 underline"
        >
          Disable auto-summon for this session
        </button>
        <button
          onClick={onAcknowledge}
          className="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1
                     rounded text-xs"
        >
          OK, got it
        </button>
      </div>
    </div>
  );
};
